import React, { useState } from "react";
import { Container } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import MuiButton from "@material-ui/core/Button";
import {
    Alert,
    AlertTitle,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Radio,
    RadioGroup,
} from "@mui/material";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Layout from "../../Shared/Layout";
import BackButton from "../../Shared/BackButton";
import { Trans } from "../../utils/i18n";
import i18n from "../../utils/i18n";
import axiosClient from "../../utils/axios";

const WarrantyCheckFormSchema = z.object({
    type: z.enum(["serialnumber", "vin", "hubmotor"], {
        message: i18n.t("validation.enum"),
        invalid_type_error: i18n.t("validation.enum"),
        required_error: i18n.t("validation.enum"),
    }),
    number: z
        .string({
            message: i18n
                .t("validation.required")
                .replace(
                    ":Attribute",
                    i18n.t("warrantyCheck.serialNumberOrVin")
                ),
            required_error: i18n
                .t("validation.required")
                .replace(
                    ":Attribute",
                    i18n.t("warrantyCheck.serialNumberOrVin")
                ),
        })
        .min(1, {
            message: i18n
                .t("validation.required")
                .replace(
                    ":Attribute",
                    i18n.t("warrantyCheck.serialNumberOrVin")
                ),
        }),
});

export default function WarrantyIndexPage({ user }) {
    const [formValues, setFormValues] = useState({
        type: "serialnumber",
        number: "",
    });
    const [formErrors, setFormErrors] = useState({});
    const {
        mutate: checkSerialnumber,
        isLoading: isCheckSerialnumberLoading,
        data: checkSerialnumberData,
        status: checkSerialnumberStatus,
        error: checkSerialnumberError,
    } = useMutation({
        mutationFn: (formValues) =>
            axiosClient
                .post(route("warranty.store"), formValues)
                .then((response) => response.data)
    });

    const onSubmit = (event) => {
        event.preventDefault();
        setFormErrors({});

        const validationResult = WarrantyCheckFormSchema.safeParse(formValues);

        if (!validationResult.success) {
            setFormErrors(validationResult.error.flatten());
            return;
        }

        checkSerialnumber(validationResult.data);
    };

    return (
        <Container maxWidth="lg">
            <div className="space-y-6">
                {user ? (
                    <BackButton />
                ) : (
                    <BackButton
                        href={route("home")}
                        label={<Trans i18nKey="common.home">Home</Trans>}
                    />
                )}

                <div className="space-y-2">
                    <h1 className="my-0 leading-tight">
                        <Trans i18nKey="warrantyCheck.title">
                            Warranty Check
                        </Trans>
                    </h1>

                    <p className="my-0 leading-normal text-gray-500">
                        <Trans i18nKey="warrantyCheck.description">
                            Enter the product numbers to check their warranty
                            status. You can enter multiple numbers, one per
                            line.
                        </Trans>
                    </p>
                </div>

                <form className="space-y-4" onSubmit={onSubmit}>
                    <FormControl
                        component="fieldset"
                        error={!!formErrors?.fieldErrors?.type?.[0]}
                    >
                        <FormLabel component="legend">
                            <Trans i18nKey="warrantyCheck.type">Type</Trans>
                        </FormLabel>
                        <RadioGroup
                            aria-label="type"
                            name="type"
                            value={formValues.type}
                            onChange={(e) =>
                                setFormValues({
                                    ...formValues,
                                    type: e.target.value,
                                })
                            }
                            required={true}
                        >
                            <FormControlLabel
                                value="serialnumber"
                                control={<Radio color="primary" />}
                                label={
                                    <Trans i18nKey="serialNumber">
                                        Serial number
                                    </Trans>
                                }
                            />
                            { /*<FormControlLabel
                                value="vin"
                                control={<Radio color="primary" />}
                                label={
                                    <Trans i18nKey="vin">
                                        VIN (Vehicle Identification Number)
                                    </Trans>
                                }
                            /> */}
                            <FormControlLabel
                                value="hubmotor"
                                control={<Radio color="primary" />}
                                label={
                                    <Trans i18nKey="hubmotorOrVin">
                                        Hub motor number or VIN (Vehicle Identification Number)
                                    </Trans>
                                }
                            />
                        </RadioGroup>
                        {formErrors?.fieldErrors?.type?.[0] && (
                            <FormHelperText className="!text-base">
                                {formErrors?.fieldErrors?.type?.[0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <TextField
                        error={!!formErrors?.fieldErrors?.number?.[0]}
                        helperText={formErrors?.fieldErrors?.number?.[0]}
                        label={
                            <Trans i18nKey="warrantyCheck.textFieldLabel">
                                Enter Serialnumber or Vehicle Identification
                                Number (VIN)
                            </Trans>
                        }
                        name="number"
                        type="text"
                        onChange={(e) =>
                            setFormValues({
                                ...formValues,
                                number: e.target.value,
                            })
                        }
                        variant="outlined"
                        value={formValues.number}
                    />

                    <div className="flex justify-end items-center">
                        <MuiButton
                            variant="contained"
                            color="secondary"
                            size="large"
                            type="submit"
                            disabled={isCheckSerialnumberLoading}
                            startIcon={
                                isCheckSerialnumberLoading && (
                                    <CircularProgress size={14} />
                                )
                            }
                        >
                            <Trans i18nKey="warrantyCheck.submit">
                                Check Warranty
                            </Trans>
                        </MuiButton>
                    </div>
                </form>

                {(!!checkSerialnumberError || !!checkSerialnumberData) && (
                    <Divider className="my-8" />
                )}

                {!!checkSerialnumberError && (
                    <Alert severity="error">
                        <AlertTitle>
                            {checkSerialnumberError?.response?.data
                                ?.message || (
                                <Trans i18nKey="warrantyCheck.error">
                                    Error
                                </Trans>
                            )}
                        </AlertTitle>

                        {checkSerialnumberError?.response?.data?.errors && (
                            <ul>
                                {Object.values(
                                    checkSerialnumberError?.response?.data
                                        ?.errors
                                ).map((errorsArray, index) => (
                                    <React.Fragment key={index}>
                                        {errorsArray.map((error, index) => (
                                            <li key={error}>{error}</li>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </ul>
                        )}
                    </Alert>
                )}

                {!!checkSerialnumberData?.data && (
                    <TableContainer>
                        <Table sx={{ minWidth: 650 }} aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell className="!font-bold">
                                        {formValues.type === "serialnumber" ? (
                                            <Trans i18nKey="serialNumber">
                                                Serial number
                                            </Trans>
                                        ) : (
                                            <Trans i18nKey="vin">
                                                VIN (Vehicle Identification Number)
                                            </Trans>
                                        )}
                                    </TableCell>
                                    <TableCell wid className="!font-bold">
                                        <Trans i18nKey="common.productNumber">
                                            Product number
                                        </Trans>
                                    </TableCell>
                                    <TableCell wid className="!font-bold">
                                        <Trans i18nKey="common.productDescription">
                                            Product description
                                        </Trans>
                                    </TableCell>
                                    <TableCell wid className="!font-bold">
                                        <Trans i18nKey="warrantyCheck.warranyStatus">
                                            Status
                                        </Trans>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[
                                    ...(checkSerialnumberData?.data ?? []),

                                    // We didn't receive a status for the number, so obviously it was not found.
                                    (checkSerialnumberData?.data ?? []).length === 0 ? (
                                        {
                                            serialnumber: formValues.number,
                                            vin: formValues.number,
                                            warranty: "not_found",
                                        }
                                    ) : null,
                                ]?.filter(Boolean)?.map((row) => (
                                    <TableRow
                                        hover={true}
                                        key={row.serialnumber}
                                        sx={{
                                            "&:last-child td, &:last-child th":
                                                { border: 0 },
                                        }}
                                    >
                                        <TableCell component="th" scope="row">
                                            <div className="flex items-center gap-2">
                                                {row.warranty ===
                                                "in_warranty" ? (
                                                    <CheckIcon color="success" />
                                                ) : row.warranty ===
                                                  "out_of_warranty" ? (
                                                    <CloseIcon color="error" />
                                                ) : (
                                                    <CloseIcon color="error" />
                                                )}
                                                {row.serialnumber}
                                            </div>
                                        </TableCell>
                                        <TableCell component="th" scope="row">
                                            {row.productNumber}
                                        </TableCell>
                                        <TableCell component="th" scope="row">
                                            {row.productDescription}
                                        </TableCell>
                                        <TableCell>
                                            {row.warranty === "in_warranty" ? (
                                                <Trans i18nKey="warrantyCheck.inWarranty">
                                                    In Warranty
                                                </Trans>
                                            ) : row.warranty ===
                                              "out_of_warranty" ? (
                                                <Trans i18nKey="warrantyCheck.outOfWarranty">
                                                    Out of Warranty
                                                </Trans>
                                            ) : (
                                                <Trans i18nKey="warrantyCheck.warrantyNotFound">
                                                    Not found
                                                </Trans>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </div>
        </Container>
    );
}

WarrantyIndexPage.layout = (page) => <Layout children={page} />;
